import { useState } from "react";
import { useToast } from "../../ui/use-toast";
import { supabase } from "../../../lib/supabase";
import { Dialog, DialogContent } from "../../ui/dialog";
import { WrittenThought } from "./WrittenThought";
import { RecordingInterface } from "../shared/RecordingInterface";
import { Mic } from "lucide-react";
import { transcribeAudio } from "../../../lib/transcription";
import { processStatusService } from "../../../services/supabase/process-status";
import { useNavigate } from "react-router-dom";

interface Tag {
  id: string;
  name: string;
}

interface CaptureThoughtProps {
  isTyping: boolean;
  setIsTyping: (value: boolean) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CaptureThought({ isTyping, setIsTyping, open, onOpenChange }: CaptureThoughtProps) {
  const [thought, setThought] = useState("");
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSave = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error("User not authenticated");

      // Create meeting record for the thought
      const { data: meeting, error: meetingError } = await supabase
        .from('meetings')
        .insert({
          user_id: user.id,
          title: thought.substring(0, 50) + "...",
          status: 'processing',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (meetingError) throw meetingError;

      // Initialize status tracking
      await processStatusService.updateStatus(meeting.id, user.id, 'upload', 'processing');

      const { error: thoughtError } = await supabase
        .from('thoughts')
        .insert({
          user_id: user.id,
          content: thought,
          type: 'written',
          tags: selectedTags.map(tag => tag.name).join(','),
          manually_linked_meeting: meeting.id
        });

      if (thoughtError) throw thoughtError;

      // Mark upload as completed
      await processStatusService.updateStatus(meeting.id, user.id, 'upload', 'completed');

      toast({
        title: "Thought saved",
        description: (
          <div className="flex flex-col gap-2">
            <span>Your thought has been saved successfully.</span>
            <button
              onClick={() => navigate('/app/status')}
              className="text-sm text-blue-500 hover:text-blue-600"
            >
              View Status
            </button>
          </div>
        ),
      });

      setThought("");
      setSelectedTags([]);
      setIsTyping(false);
      onOpenChange(false);
      navigate('/app/status');
    } catch (error: any) {
      console.error('Error saving thought:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save thought",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    setThought("");
    setSelectedTags([]);
    setIsTyping(false);
    setIsRecording(false);
    onOpenChange(false);
  };

  const handleRecordingComplete = async (blob: Blob) => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error("User not authenticated");

      const transcription = await transcribeAudio(blob, user.id, 'thought');
      
      toast({
        title: "Recording complete",
        description: (
          <div className="flex flex-col gap-2">
            <span>Your recording has been saved and is being transcribed.</span>
            <button
              onClick={() => navigate('/app/status')}
              className="text-sm text-blue-500 hover:text-blue-600"
            >
              View Status
            </button>
          </div>
        ),
      });
      
      setIsRecording(false);
      onOpenChange(false);
      navigate('/app/status');
    } catch (error: any) {
      console.error('Error processing recording:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process recording",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] p-6 bg-background border-border min-h-[300px]">
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold text-foreground">Capture a Thought</h2>
          <p className="text-sm text-muted-foreground">
            Do you have something you want to remember? Or maybe some context or background
            information you want to add? You can type or talk out your thought here.
          </p>
          
          {!isTyping && !isRecording && (
            <div className="relative">
              <input
                type="text"
                placeholder="Type your thought here..."
                className="w-full px-4 py-3 pr-12 rounded-full bg-background border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                onClick={() => setIsTyping(true)}
              />
              <button
                onClick={() => setIsRecording(true)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted transition-colors"
              >
                <Mic className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
          )}

          {isTyping && (
            <WrittenThought
              thought={thought}
              onThoughtChange={setThought}
              onSave={handleSave}
              onCancel={handleClose}
            />
          )}

          {isRecording && (
            <RecordingInterface
              onCancel={() => setIsRecording(false)}
              onComplete={handleRecordingComplete}
              title="Record a Thought"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}